/* global React, Icon, Button, Modal, Field, Input, useState, useEffect, useRef, t, toast */
// ============================================================
// TEAMCHAT — intern chat i regnskapsbyrået
// ============================================================
// Fellesrom, egne rom, og direktemeldinger mellom ansatte.
//
// Designet holdt meldingene i React-state; her ligger de i Firestore under
// `accountingFirms/{firmId}/chatRooms/*`, slik at en samtale overlever at fanen
// lukkes og faktisk når fram til den andre. Skrivingen går direkte fra klienten med
// samme begrunnelse som kundedialogene — se firestore.rules.

const TC_COMMON_ROOM_ID = 'felles';

const tcInitials = (name) => String(name || '')
  .trim()
  .split(/\s+/)
  .map((word) => word[0] || '')
  .join('')
  .toUpperCase()
  .slice(0, 2);

const tcTime = (isoString) => {
  const value = Date.parse(String(isoString || ''));
  if (!Number.isFinite(value)) return '';
  const date = new Date(value);
  const sameDay = new Date().toDateString() === date.toDateString();
  return sameDay
    ? date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' }) + ' '
      + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

function TcNewRoomModal({ open, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setName(''); }, [open]);
  const submit = async () => {
    const value = name.trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      await onCreate(value);
    } finally {
      setBusy(false);
    }
  };
  return <Modal
    open={open}
    onClose={onClose}
    title={t('tc.newRoom', 'Nytt rom')}
    max={440}
    footer={<React.Fragment>
      <Button variant="ghost" onClick={onClose}>{t('common.cancel', 'Avbryt')}</Button>
      <Button icon="plus" disabled={!name.trim() || busy} onClick={submit} data-help={t('helpTopics.chatVisibility.body')} data-help-title={t('helpTopics.chatVisibility.title')}>{t('tc.createRoom', 'Opprett rom')}</Button>
    </React.Fragment>}
  >
    <Field label={t('tc.roomName', 'Romnavn')} help={t('helpTopics.chatVisibility.body')} helpTitle={t('helpTopics.chatVisibility.title')}>
      <Input
        autoFocus
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder={t('tc.roomNamePlaceholder', 'F.eks. Kontroll-teamet')}
        onKeyDown={(event) => { if (event.key === 'Enter') void submit(); }}
      />
    </Field>
  </Modal>;
}

function TcRoomList({ rooms, colleagues, activeId, onSelect, onNewRoom }) {
  return <div className="tc-sidebar">
    <div className="tc-sb-head">{t('tc.title', 'Teamchat')}</div>
    <div className="tc-sb-section">
      <div className="tc-sb-label">{t('tc.rooms', 'Rom')}</div>
      {rooms.map((room) => <button
        key={room.id}
        type="button"
        className={'tc-sb-item' + (activeId === room.id ? ' active' : '')}
        onClick={() => onSelect(room.id)}
      >
        <span className="tc-sb-ico"><Icon name={room.id === TC_COMMON_ROOM_ID ? 'users' : 'list'} size={16} /></span>
        <span className="tc-sb-name">{room.name}</span>
      </button>)}
      <button type="button" className="tc-sb-item tc-sb-add" onClick={onNewRoom}>
        <span className="tc-sb-ico"><Icon name="plus" size={16} /></span>
        <span className="tc-sb-name">{t('tc.newRoom', 'Nytt rom')}</span>
      </button>
    </div>
    <div className="tc-sb-section">
      <div className="tc-sb-label">{t('tc.dms', 'Direktemeldinger')}</div>
      {colleagues.length === 0
        ? <div className="tc-sb-empty">{t('tc.noColleagues', 'Ingen andre i byrået ennå')}</div>
        : colleagues.map((person) => <button
          key={person.userId}
          type="button"
          className={'tc-sb-item' + (activeId === person.roomId ? ' active' : '')}
          onClick={() => onSelect(person.roomId)}
        >
          <span className="tc-sb-ava">{tcInitials(person.name)}</span>
          <span className="tc-sb-name">{person.name}</span>
        </button>)}
    </div>
  </div>;
}

function TcThread({ title, subtitle, messages, myUserId, onSend, disabled }) {
  const [value, setValue] = useState('');
  const endRef = useRef(null);
  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ block: 'end' });
  }, [messages.length]);
  const send = () => {
    const text = value.trim();
    if (!text) return;
    setValue('');
    void onSend(text);
  };
  return <div className="tc-main">
    <div className="tc-main-hd">
      <b className="t-h3" style={{ fontSize: 16 }}>{title}</b>
      <div className="t-xs muted" style={{ fontWeight: 700 }}>{subtitle}</div>
    </div>
    <div className="tc-body">
      {messages.length === 0
        ? <div className="tc-empty">{t('tc.empty', 'Ingen meldinger her ennå — skriv den første.')}</div>
        : null}
      {messages.map((message) => {
        const mine = message.authorUserId === myUserId;
        return <div key={message.id} className={'tc-msg ' + (mine ? 'me' : 'them')}>
          {mine ? null : <span className="tc-msg-from">{message.authorName || t('tc.unknown', 'Ukjent')}</span>}
          <div className="tc-msg-bubble">{message.text}</div>
          <span className="tc-msg-time">{tcTime(message.createdAt)}</span>
        </div>;
      })}
      <div ref={endRef} />
    </div>
    <div className="tc-foot">
      <input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder={t('tc.placeholder', 'Skriv en melding …')}
        onKeyDown={(event) => { if (event.key === 'Enter') send(); }}
        disabled={disabled}
      />
      <Button icon="send" onClick={send} disabled={disabled} data-help={t('helpTopics.chatVisibility.body')} data-help-title={t('helpTopics.chatVisibility.title')}>{t('tc.send', 'Send')}</Button>
    </div>
  </div>;
}

function TeamchatPage({ firmId, members = [] }) {
  const backend = window.PlummeBackend ? window.PlummeBackend.useBackend() : {};
  const myUserId = backend.user?.uid || '';
  const [rooms, setRooms] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeId, setActiveId] = useState(TC_COMMON_ROOM_ID);
  const [newRoomOpen, setNewRoomOpen] = useState(false);

  // Kollegene kommer fra byråets medlemsliste. Den er bare med i svaret for
  // eier/admin; en ansatt får derfor rom, men ikke direktemeldinger, til
  // medlemslisten er tilgjengelig for alle.
  const colleagues = members
    .filter((member) => member.userId && member.userId !== myUserId && member.status === 'active')
    .map((member) => ({
      userId: member.userId,
      name: member.displayName || member.email || member.userId,
      title: member.title || '',
      roomId: window.PlummeBackend?.firmDmRoomId?.(myUserId, member.userId) || '',
    }))
    .filter((person) => person.roomId);

  useEffect(() => {
    if (!firmId) return undefined;
    return window.PlummeBackend?.subscribeFirmChatRooms?.(firmId, setRooms);
  }, [firmId]);

  useEffect(() => {
    if (!firmId || !activeId) return undefined;
    setMessages([]);
    return window.PlummeBackend?.subscribeFirmChatMessages?.(firmId, activeId, setMessages);
  }, [firmId, activeId]);

  // Fellesrommet finnes alltid, også før noen har skrevet i det. Det opprettes
  // først når den første meldingen sendes — et tomt dokument uten innhold gir ingen.
  const roomList = [
    { id: TC_COMMON_ROOM_ID, name: t('tc.commonRoom', 'Fellesrom') },
    ...rooms
      .filter((room) => room.id !== TC_COMMON_ROOM_ID && room.kind !== 'dm')
      .map((room) => ({ id: room.id, name: room.name || t('tc.room', 'Rom') })),
  ];

  const newRoom = async (name) => {
    try {
      const roomId = await window.PlummeBackend?.createFirmChatRoom?.({ firmId, name });
      if (roomId) setActiveId(roomId);
      setNewRoomOpen(false);
    } catch (error) {
      toast(error?.message || String(error), 'error');
    }
  };

  const isDm = String(activeId || '').startsWith('dm__');
  const activeRoom = roomList.find((room) => room.id === activeId);
  const activeColleague = colleagues.find((person) => person.roomId === activeId);
  const title = isDm ? (activeColleague?.name || t('tc.dm', 'Direktemelding')) : (activeRoom?.name || '');
  // Designet viser rollen til den du snakker med som undertekst; byrået lagrer den
  // som `title` på medlemmet, og firmTitleLabel gir '—' når den mangler.
  const dmTitle = activeColleague?.title && window.firmTitleLabel
    ? window.firmTitleLabel(activeColleague.title)
    : '';
  const subtitle = isDm
    ? (dmTitle && dmTitle !== '—' ? dmTitle : t('tc.dmSubtitle', 'Direktemelding'))
    : (activeId === TC_COMMON_ROOM_ID ? t('tc.commonSubtitle', 'Felles rom for hele teamet') : t('tc.roomSubtitle', 'Rom i byrået'));

  const send = async (text) => {
    try {
      await window.PlummeBackend?.sendFirmChatMessage?.({
        firmId,
        roomId: activeId,
        text,
        roomName: isDm ? '' : (activeRoom?.name || ''),
      });
    } catch (error) {
      toast(error?.message || String(error), 'error');
    }
  };

  return <div className="tc-wrap">
    <TcRoomList
      rooms={roomList}
      colleagues={colleagues}
      activeId={activeId}
      onSelect={setActiveId}
      onNewRoom={() => setNewRoomOpen(true)}
    />
    <TcThread
      title={title}
      subtitle={subtitle}
      messages={messages}
      myUserId={myUserId}
      onSend={send}
      disabled={!firmId}
    />
    <TcNewRoomModal open={newRoomOpen} onClose={() => setNewRoomOpen(false)} onCreate={newRoom} />
  </div>;
}

Object.assign(window, { TeamchatPage });
